// ══════════════════════════════════════════════════════════════════════════════�?// Home Stock �?AI photo selection & delete-photo confirmation modals
// ══════════════════════════════════════════════════════════════════════════════�?
import { useEffect, useMemo, useState } from 'react';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import {
  MODAL_BACKDROP_CENTER_CLASS,
  MODAL_BACKDROP_SCRIM_CLASS,
  MODAL_PANEL_SCROLL_DVH_CLASS,
} from '../../lib/modalClasses';
import { AppButton } from '../ui/AppButton';
import { StocktakeUploadedPhotoThumb } from './StocktakeUploadedPhotoThumb';

export type StocktakePhotoModalItem = {
  id: string;
  displayUrl: string;
  canonicalUrl: string;
  aiAnalysisCompleted: boolean;
  authedApiPath?: string;
};

type InventoryStocktakeAiSelectModalProps = {
  open: boolean;
  photos: StocktakePhotoModalItem[];
  t: (key: string, params?: Record<string, string | number>) => string;
  onClose: () => void;
  onConfirm: (selectedPhotoIds: string[]) => void;
  busy?: boolean;
};

export const InventoryStocktakeAiSelectModal = ({
  open,
  photos,
  t,
  onClose,
  onConfirm,
  busy = false,
}: InventoryStocktakeAiSelectModalProps) => {
  const notAnalyzed = useMemo(
    () => photos.filter((p) => !p.aiAnalysisCompleted),
    [photos],
  );
  const analyzed = useMemo(
    () => photos.filter((p) => p.aiAnalysisCompleted),
    [photos],
  );

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) {
      return;
    }
    setSelectedIds(new Set(notAnalyzed.map((p) => p.id)));
  }, [open, notAnalyzed]);

  if (!open) {
    return null;
  }

  const handleToggle = (photoId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(photoId)) {
        next.delete(photoId);
      } else {
        next.add(photoId);
      }
      return next;
    });
  };

  const handleConfirm = () => {
    onConfirm([...selectedIds]);
  };

  const renderSection = (
    heading: string,
    sectionPhotos: StocktakePhotoModalItem[],
  ) => (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-bold text-gray-900 dark:text-gray-50">{heading}</h4>
      {sectionPhotos.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('inventory.wizard.aiSelectModalSectionEmpty')}</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {sectionPhotos.map((photo, index) => {
            const checked = selectedIds.has(photo.id);
            return (
              <li key={photo.id}>
                <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-gray-200 bg-white p-2 dark:border-gray-600 dark:bg-gray-900">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => handleToggle(photo.id)}
                    disabled={busy}
                    className="h-4 w-4 shrink-0 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    aria-label={t('inventory.wizard.aiSelectPhotoCheckboxAria', { n: index + 1 })}
                  />
                  <StocktakeUploadedPhotoThumb
                    displayUrl={photo.displayUrl}
                    authedApiPath={photo.authedApiPath}
                    imageAlt={t('inventory.uploadedStocktakePhotoAlt', { n: index + 1 })}
                    enlargeAriaLabel={t('inventory.enlargePhotoAria')}
                    closeViewerLabel={t('common.closeViewer')}
                    removeAriaLabel=""
                    onRequestRemove={() => undefined}
                    actionsDisabled
                  />
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-100">
                    {t('inventory.uploadedStocktakePhotoAlt', { n: index + 1 })}
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );

  return (
    <div
      className={`${MODAL_BACKDROP_CENTER_CLASS} z-[100] ${MODAL_BACKDROP_SCRIM_CLASS} p-4`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="inv-ai-select-modal-title"
    >
      <div
        className={`${MODAL_PANEL_SCROLL_DVH_CLASS} flex max-h-[min(90dvh,720px)] w-full max-w-lg flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-900`}
      >
        <h3 id="inv-ai-select-modal-title" className="text-lg font-bold text-gray-900 dark:text-gray-100">
          {t('inventory.wizard.aiSelectModalTitle')}
        </h3>
        <p className="text-sm text-gray-600 dark:text-gray-300">{t('inventory.wizard.aiSelectModalDescription')}</p>
        {renderSection(t('inventory.wizard.aiSelectModalNotAnalyzedHeading'), notAnalyzed)}
        {renderSection(t('inventory.wizard.aiSelectModalAnalyzedHeading'), analyzed)}
        <div className="flex flex-col gap-2 sm:flex-row">
          <AppButton type="button" variant="secondary" onClick={onClose} disabled={busy} className="flex-1">
            {t('common.cancel')}
          </AppButton>
          <AppButton type="button" variant="primary" onClick={handleConfirm} disabled={busy} className="flex-1">
            {t('inventory.wizard.aiSelectModalConfirm')}
          </AppButton>
        </div>
      </div>
    </div>
  );
};

type InventoryDeleteStocktakePhotoDialogProps = {
  open: boolean;
  t: (key: string, params?: Record<string, string | number>) => string;
  linkedItemCount: number;
  onCancel: () => void;
  onKeepPhotoAndItems: () => void;
  onDeletePhotoAndItems: () => void;
};

export const InventoryDeleteStocktakePhotoDialog = ({
  open,
  t,
  linkedItemCount,
  onCancel,
  onKeepPhotoAndItems,
  onDeletePhotoAndItems,
}: InventoryDeleteStocktakePhotoDialogProps) => {
  if (!open) {
    return null;
  }

  return (
    <div
      className={`${MODAL_BACKDROP_CENTER_CLASS} z-[110] ${MODAL_BACKDROP_SCRIM_CLASS} p-4`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="inv-delete-photo-modal-title"
    >
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-4 shadow-xl dark:border-gray-700 dark:bg-gray-900">
        <h3 id="inv-delete-photo-modal-title" className="text-lg font-bold text-gray-900 dark:text-gray-100">
          {t('inventory.removeStocktakePhotoTitle')}
        </h3>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {linkedItemCount > 0
            ? t('inventory.removeStocktakePhotoWithItemsDescription', { count: linkedItemCount })
            : t('inventory.removeStocktakePhotoDescription')}
        </p>
        <div className="mt-4 flex flex-col gap-2">
          {linkedItemCount > 0 ? (
            <AppButton type="button" variant="danger" fullWidth onClick={onDeletePhotoAndItems}>
              {t('inventory.removeStocktakePhotoDeleteWithItems')}
            </AppButton>
          ) : null}
          <AppButton type="button" variant="secondary" fullWidth onClick={onKeepPhotoAndItems}>
            {linkedItemCount > 0
              ? t('inventory.removeStocktakePhotoKeepAll')
              : t('common.cancel')}
          </AppButton>
          {linkedItemCount > 0 ? (
            <AppButton type="button" variant="ghost" fullWidth onClick={onCancel}>
              {t('common.cancel')}
            </AppButton>
          ) : (
            <AppButton type="button" variant="danger" fullWidth onClick={onDeletePhotoAndItems}>
              {t('inventory.removeStocktakePhotoConfirm')}
            </AppButton>
          )}
        </div>
      </div>
    </div>
  );
};

type InventoryReanalyzePhotoConfirmDialogProps = {
  open: boolean;
  photoCount: number;
  itemCount: number;
  t: (key: string, params?: Record<string, string | number>) => string;
  onCancel: () => void;
  onConfirm: () => void;
};

export const InventoryReanalyzePhotoConfirmDialog = ({
  open,
  photoCount,
  itemCount,
  t,
  onCancel,
  onConfirm,
}: InventoryReanalyzePhotoConfirmDialogProps) => {
  if (!open) {
    return null;
  }

  return (
    <ConfirmDialog
      open={open}
      title={t('inventory.wizard.reanalyzeConfirmTitle')}
      description={t('inventory.wizard.reanalyzeConfirmDescription', {
        photos: photoCount,
        items: itemCount,
      })}
      confirmLabel={t('inventory.wizard.reanalyzeConfirmProceed')}
      cancelLabel={t('common.cancel')}
      backdropZClass="z-[110]"
      variant="danger"
      onConfirm={onConfirm}
      onCancel={onCancel}
    />
  );
};
